import React from "react";
import { useNavigate } from "react-router-dom";
import { useDocumentDispatch } from "context/Documents";
import app from "./realm";

const getCollection = () => {
  const mongodb = app.currentUser.mongoClient("mongodb-atlas");
  return mongodb.db("writeai").collection("documents");
};

const getDate = () => {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

// Normalise a MongoDB document to have a string `id` field
const normalise = (doc) => {
  if (!doc) return null;
  return { ...doc, id: doc._id?.toString() || doc.id };
};

const VALID_TYPES = ["research", "business", "literature", "case-study", "technical"];

export const GetDocuments = () => {
  const dispatch = useDocumentDispatch();
  const [documentsLoading, setLoading] = React.useState(false);
  const [documentsData, setData] = React.useState(null);

  React.useEffect(() => {
    const fetch = async () => {
      if (!app.currentUser) return;
      setLoading(true);
      try {
        const col = getCollection();
        const docs = await col.find({ userId: app.currentUser.id });
        const normalised = docs.map(normalise);
        dispatch({ type: "DOCUMENTS", payload: normalised });
        setData(normalised);
      } catch (err) {
        // unauthenticated or network error
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return { documentsData, documentsLoading };
};

export const GetDocument = (id) => {
  const dispatch = useDocumentDispatch();
  const [documentLoading, setLoading] = React.useState(false);
  const [documentData, setData] = React.useState(null);

  React.useEffect(() => {
    const fetch = async () => {
      if (!id || !app.currentUser) return;
      setLoading(true);
      try {
        const col = getCollection();
        // Realm BSON — convert string id to ObjectId for query
        const { BSON } = require("realm-web");
        const doc = await col.findOne({
          _id: new BSON.ObjectId(id),
          userId: app.currentUser.id,
        });
        const normalised = normalise(doc);
        if (normalised) {
          dispatch({ type: "UPDATE", payload: normalised });
          setData({ getDocument: normalised });
        }
      } catch (err) {
        // handle silently
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  return { documentData, documentLoading };
};

export const CreateDocument = (setErrors) => {
  const dispatch = useDocumentDispatch();
  const navigate = useNavigate();
  const [createLoading, setLoading] = React.useState(false);

  const createDocument = async ({ variables }) => {
    const { type } = variables;
    if (!VALID_TYPES.includes(type)) {
      if (setErrors) setErrors({ general: "Invalid document type" });
      return;
    }
    setLoading(true);
    try {
      const col = getCollection();
      const today = getDate();
      const result = await col.insertOne({
        type,
        userId: app.currentUser.id,
        title: "",
        content: "",
        references: "[]",
        background: "",
        isPublic: true,
        lastEdited: today,
        lastViewed: today,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      const doc = normalise({ _id: result.insertedId, type, title: "", content: "",
        references: "[]", background: "", isPublic: true, lastEdited: today,
        lastViewed: today, userId: app.currentUser.id });
      dispatch({ type: "CREATE", payload: doc });
      navigate(`/page/${doc.id}`, { replace: true });
    } catch (err) {
      if (setErrors) setErrors({ general: err.message || "Create failed" });
    } finally {
      setLoading(false);
    }
  };

  return { createDocument, createLoading };
};

export const UpdateDocument = (setErrors) => {
  const dispatch = useDocumentDispatch();
  const [updateLoading, setLoading] = React.useState(false);

  const updateDocument = async ({ variables }) => {
    const { id, ...fields } = variables;
    if (!id) return;
    setLoading(true);
    try {
      const col = getCollection();
      const { BSON } = require("realm-web");
      const update = { ...fields, lastEdited: getDate(), updatedAt: new Date() };
      // Remove undefined values
      Object.keys(update).forEach((k) => update[k] === undefined && delete update[k]);

      await col.updateOne(
        { _id: new BSON.ObjectId(id), userId: app.currentUser.id },
        { $set: update }
      );

      const updated = normalise({ _id: { toString: () => id }, id, ...fields, ...update });
      dispatch({ type: "UPDATE", payload: updated });
    } catch (err) {
      if (setErrors) setErrors({ general: err.message || "Update failed" });
    } finally {
      setLoading(false);
    }
  };

  return { updateDocument, updateLoading };
};

export const DeleteDocument = (setErrors) => {
  const dispatch = useDocumentDispatch();
  const [deleteLoading, setLoading] = React.useState(false);

  const deleteDocument = async ({ variables }) => {
    const { id } = variables;
    setLoading(true);
    try {
      const col = getCollection();
      const { BSON } = require("realm-web");
      await col.deleteOne({ _id: new BSON.ObjectId(id), userId: app.currentUser.id });
      dispatch({ type: "DELETE", payload: { id } });
    } catch (err) {
      if (setErrors) setErrors({ general: err.message || "Delete failed" });
    } finally {
      setLoading(false);
    }
  };

  return { deleteDocument, deleteLoading };
};
